/* eslint-disable react-hooks/exhaustive-deps -- effects intentionally run on mount / on the listed deps; adding the flagged functions risks render loops. */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  CloudOff as CloudOffIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { calendarAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';
import { formatDistanceToNow } from 'date-fns';

interface GoogleCalendarSyncProps {
  onSyncComplete?: () => void;
}

interface GoogleCalendar {
  id: string;
  summary: string;
  primary: boolean;
  access_role: string;
  background_color: string;
  foreground_color: string;
}

interface SyncStatus {
  connected: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  calendars: string[];
}

const GoogleCalendarSync: React.FC<GoogleCalendarSyncProps> = ({ onSyncComplete }) => {
  const { confirm } = useUI();
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [calendars, setCalendars] = useState<GoogleCalendar[]>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await calendarAPI.getGoogleCalendarStatus();
      setStatus(response.data);

      if (response.data.connected) {
        await loadCalendars();
        setSelectedCalendars(response.data.calendars || ['primary']);
      }
    } catch (err: any) {
      console.error('Error loading Google Calendar status:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCalendars = async () => {
    try {
      const response = await calendarAPI.listGoogleCalendars();
      setCalendars(response.data);
    } catch (err: any) {
      console.error('Error loading calendars:', err);
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await calendarAPI.getGoogleAuthUrl();
      const authUrl = response.data.auth_url;

      // Open Google OAuth in new window
      window.location.href = authUrl;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initiate Google Calendar connection');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    const ok = await confirm({
      title: 'Disconnect Google Calendar?',
      message: 'We will stop syncing events from your Google account.',
      confirmLabel: 'Disconnect',
      destructive: true,
    });
    if (!ok) return;

    try {
      setLoading(true);
      setError(null);

      await calendarAPI.disconnectGoogleCalendar();
      setSuccess('Google Calendar disconnected successfully');
      setStatus(null);
      setCalendars([]);
      setSelectedCalendars([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncResult(null);

      const response = await calendarAPI.syncGoogleCalendar({
        calendar_ids: selectedCalendars.length > 0 ? selectedCalendars : ['primary'],
        days_ahead: 30,
      });

      setSyncResult(response.data);
      setSuccess(`Sync complete! ${response.data.events_created} events created, ${response.data.events_updated} updated`);

      if (onSyncComplete) {
        onSyncComplete();
      }

      await loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sync Google Calendar');
    } finally {
      setSyncing(false);
    }
  };

  const handleCalendarToggle = (calendarId: string) => {
    setSelectedCalendars(prev =>
      prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  const handleSelectCalendars = () => {
    setCalendarDialogOpen(true);
  };

  const handleSaveCalendarSelection = () => {
    setCalendarDialogOpen(false);
  };

  if (loading && !status) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <GoogleIcon color="primary" />
          <Typography variant="h6">Google Calendar</Typography>
        </Box>

        {status?.connected && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Connected"
            color="success"
            size="small"
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!status?.connected ? (
        <Box>
          <Typography variant="body2" color="textSecondary" paragraph>
            Connect your Google Calendar to automatically sync events and get AI-powered outfit recommendations for each event.
          </Typography>

          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={handleConnect}
            disabled={loading}
          >
            {loading ? 'Connecting...' : 'Connect Google Calendar'}
          </Button>
        </Box>
      ) : (
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing || selectedCalendars.length === 0}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleSelectCalendars}
              disabled={syncing}
            >
              Select Calendars ({selectedCalendars.length})
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<CloudOffIcon />}
              onClick={handleDisconnect}
              disabled={loading || syncing}
            >
              Disconnect
            </Button>
          </Box>

          {status.last_sync_at && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Last synced {formatDistanceToNow(new Date(status.last_sync_at), { addSuffix: true })}
            </Typography>
          )}

          {syncResult && (
            <Box mt={2}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Sync Results:
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip
                  label={`${syncResult.total_events} Total Events`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${syncResult.events_requiring_outfits} Require Outfits`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${syncResult.events_created} Created`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  label={`${syncResult.events_updated} Updated`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
                <Chip
                  label={`${syncResult.events_skipped} Skipped`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Calendar Selection Dialog */}
      <Dialog
        open={calendarDialogOpen}
        onClose={() => setCalendarDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Calendars to Sync</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Choose which calendars you want to sync events from:
          </Typography>

          <List>
            {calendars.map((calendar) => (
              <ListItem
                key={calendar.id}
                dense
                disablePadding
              >
                <ListItemButton onClick={() => handleCalendarToggle(calendar.id)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedCalendars.includes(calendar.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={calendar.summary}
                    secondary={calendar.primary ? 'Primary Calendar' : calendar.access_role}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {calendars.length === 0 && (
            <Alert severity="info">
              No calendars found. Please check your Google Calendar permissions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCalendarSelection}
            variant="contained"
            disabled={selectedCalendars.length === 0}
          >
            Save Selection
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GoogleCalendarSync;
